import React, { useCallback, useMemo, useState } from "react";
import classnames from "classnames";
import styles from "./CommandModals/CommandModals.module.scss";
import { IModalInstance } from "@whatslly/ui-components/modals/ModalProvider";
import { BaseModal } from "@whatslly/ui-components/modals";
import type { CreateSidecar } from "@whatslly/types/sidecars-server/Sidecar";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import { useTranslation } from "react-i18next";
import { PhoneInput } from "@whatslly/ui-components/v2/BaseElements/PhoneInput";
import { CheckBox } from "@whatslly/ui-components/v2/BaseElements/CheckBox";
import type { SelectOption } from "@whatslly/types/UI-V2/BaseElements/SelectType";
import { Select } from "@whatslly/ui-components/v2/BaseElements/Select";
import { useDependencyInjector } from "@whatslly/commons/di";
import type { IOrganizationStore } from "../../services/IOrganizationStore";
import { InjectOrganizationStore } from "../../services/IOrganizationStore";
import { observer } from "mobx-react-lite";

const runnerOptions = [import.meta.env.MODE !== "production" && { label: "Local", value: "spwan" }, { label: "Kubernetes", value: "k8s" }].filter(
  Boolean
) as SelectOption<{ value: CreateSidecar["runnerType"] }>[];

interface ICreateSidecarResult {
  invites: CreateSidecar["invites"];
  runnerType: CreateSidecar["runnerType"];
  channelType: CreateSidecar["channelType"];
}

export const CreateSidecarModal: React.FC<
  IModalInstance<ICreateSidecarResult> & {
    showSettings: boolean;
    user?: { _id: string };
    channelOptions: { label: string; value: Exclude<CreateSidecar["channelType"], undefined> }[];
  }
> = observer(({ closeModal, showSettings, user, channelOptions }) => {
  const { t } = useTranslation();
  const organizationStore = useDependencyInjector<IOrganizationStore>(InjectOrganizationStore);
  const users = organizationStore.usersList.case({
    fulfilled: (users) =>
      users
        .filter((user) => user.isActive)
        .map((user) => ({ label: user.name ? `${user.name}${user.email ? ` (${user.email})` : ""}` : user.email, value: user._id })),
    pending: () => [],
    rejected: () => [],
  });

  const [inviteUid, setInviteUid] = useState(user?._id ?? "");
  const [channelType, setChannelType] = useState(channelOptions[0]);
  const [runnerType, setRunnerType] = useState(runnerOptions[0]);
  const [expectedPhone, setExpectedPhone] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [includeExpectedPhoneNumber, setIncludeExpectedPhoneNumber] = useState(false);
  const disabled = useMemo(() => !users.some((user) => user.value === inviteUid), [inviteUid, users]);
  const fullExpectedPhone = useMemo(() => `${countryCode}${expectedPhone}`, [expectedPhone, countryCode]);
  const onSubmit = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault();
      if (disabled) return;
      closeModal({
        runnerType: runnerType.value,
        invites: [{ expectedPhone: includeExpectedPhoneNumber ? fullExpectedPhone : "", uid: inviteUid }],
        channelType: channelType.value,
      });
    },
    [disabled, closeModal, runnerType.value, includeExpectedPhoneNumber, fullExpectedPhone, inviteUid, channelType.value]
  );

  const selectedUser = useMemo(() => users.find((user) => user.value === inviteUid) || null, [inviteUid, users]);

  return (
    <BaseModal closeModal={closeModal} className={styles.createSidecarModal} bodyClassName={styles.body} closeOnClickOutside={false}>
      <form onSubmit={onSubmit} className={styles.body}>
        <div className={styles.field}>
          <Select
            data-testid="invite-uid"
            value={selectedUser}
            onChange={(val: (typeof users)[number]) => setInviteUid(val.value)}
            disabled={user !== undefined}
            options={users}
            placeholder={t("sidecars.invite_user")}
            isSearchable
          />
        </div>
        {import.meta.env.MODE !== "production" && (
          <div className={styles.runnerSelection}>
            <Select<SelectOption<{ value: CreateSidecar["runnerType"] }>>
              paddingInline={"none"}
              data-testid="sidecar-runner"
              value={runnerType}
              onChange={setRunnerType}
              options={runnerOptions}
            />
          </div>
        )}
        {
          <div className={styles.runnerSelection}>
            <Select<SelectOption<{ label: string; value: Exclude<CreateSidecar["channelType"], undefined> }>>
              paddingInline={"none"}
              data-testid="sidecar-channel-type"
              value={channelType}
              onChange={setChannelType}
              options={channelOptions}
              disabled={channelOptions.length === 1}
            />
          </div>
        }
        <div className={classnames(styles.field, styles.expectedPhoneField)}>
          <CheckBox
            text={t("sidecars.invite_phone")}
            checked={includeExpectedPhoneNumber}
            onChange={(e) => setIncludeExpectedPhoneNumber(e.target.checked)}
            data-testid={"show-expected-phone-check-box"}
          />
          <div className={classnames(styles.spesificPhoneNumber, { [styles.show]: includeExpectedPhoneNumber })}>
            <PhoneInput
              countryCode={countryCode}
              phoneNumberWithOutCountryCode={expectedPhone}
              onPhoneChange={setExpectedPhone}
              onCountryCodeChange={setCountryCode}
              testid={"expected-phone"}
              disableCountryCodeAutoPick
            />
          </div>
        </div>
        <ModalButtons
          className={styles.buttons}
          isSubmitDisabled={disabled}
          testId="create-sidecar-submit"
          submitText={showSettings ? t("sidecars.next") : t("sidecars.invite")}
          onSubmit={onSubmit}
          onCancel={() => closeModal()}
        />
      </form>
    </BaseModal>
  );
});
